import { ArrowRight, CalendarCheck, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  annualReviewWindow,
  currentPeriod,
  formatPlainDate,
  isAnnualReviewOpen,
  chainSummary,
  completedStages,
  stageAwaiting,
  STAGE_LABELS,
  today,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { GetStartedCard } from '@/components/get-started';
import { TasksArt } from '@/components/illustrations';
import { StageTracker } from '@/components/assessments/stage-tracker';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { useMyTeam, useOverview, useRecognitionSuggestions } from '@/lib/analytics-api';
import { useMyCurrentSelf, usePendingAssessments } from '@/lib/assessments-api';
import { assessmentStatusVariant, PILL_CLASS } from '@/lib/badges';
import { useGoalSet } from '@/lib/goals-api';
import { useCurrentUser } from '@/lib/session';

/**
 * "What needs you today", not a wall of numbers.
 *
 * Ordered by whose turn it is: things blocking YOU first, then your team, then the
 * org. The Kredily reference leads with a directory and four bare counters, which
 * answers "how many" before anyone has asked it.
 */
export function DashboardPage() {
  const me = useCurrentUser();
  const isHr = me.role === 'HR' || me.role === 'SUPER_ADMIN';

  const { data: pending, isPending: pendingLoading } = usePendingAssessments();
  const { data: goalSet } = useGoalSet('me');
  const { data: mySelf } = useMyCurrentSelf();
  const { data: team } = useMyTeam(me.hasReports);
  const { data: suggestions } = useRecognitionSuggestions(me.hasReports);
  const { data: overview } = useOverview(isHr);

  if (pendingLoading) {
    return <PageContainer><Skeleton className="h-64 w-full" /></PageContainer>;
  }

  const myReviews = pending ?? [];
  const overdue = myReviews.filter((a) => a.managerOverdue).length;
  const teamRows = team ?? [];
  const teamGoalsPending = teamRows.filter((r) => !r.goalsAccepted).length;

  // Your own set still unpublished is the thing waiting on somebody now — there is no
  // acceptance left for the employee to do.
  const myGoalsUnpublished = goalSet?.status === 'DRAFT';
  // Your own self-assessment being open is a thing that needs YOU - the one action
  // pending never covered, which is why the dashboard used to say "nothing to do"
  // during an open window.
  const selfOpen = mySelf?.actionable ?? false;
  /**
   * Their manager has rated them and it is waiting on their yes or no.
   *
   * Counted as something to do in its own right: `actionable` is false here, because the
   * assessment is submitted and locked — so without this the dashboard said "you are all
   * caught up" at the one moment the whole chain was waiting on this person.
   */
  const awaitingMyAcceptance = (mySelf?.exists && mySelf.awaitingMyAcceptance) ?? false;
  const nothingToDo =
    myReviews.length === 0 &&
    !myGoalsUnpublished &&
    teamGoalsPending === 0 &&
    !selfOpen &&
    !awaitingMyAcceptance;

  /** The stage this person's own quarter is sitting at, for the review chain card. */
  const myStage = mySelf?.exists ? stageAwaiting(mySelf.status) : null;

  // The empty-state line, told from the real state rather than a hardcoded "opens in
  // the last week" that was wrong whenever HR forced the window or it was already
  // submitted. Only shown when nothingToDo, so the window is genuinely not actionable.
  // AWAITING_EMPLOYEE_ACCEPTANCE is deliberately absent: that one is not "with your
  // reviewers", it is with you, and it is surfaced as an action above instead.
  const reviewing =
    mySelf?.exists &&
    (mySelf.status === 'AWAITING_MANAGER' ||
      mySelf.status === 'AWAITING_HR' ||
      mySelf.status === 'AWAITING_MANAGEMENT');
  const emptyStateMessage = !goalSet
    ? 'You are all caught up.'
    : goalSet.status === 'DRAFT'
      ? 'Your manager has not sent your goals yet.'
      : mySelf?.exists && mySelf.status === 'COMPLETED'
        ? `Your ${mySelf.quarterLabel} appraisal is complete.`
        : reviewing
          ? 'Your self assessment is submitted and now with your reviewers.'
          : mySelf?.exists && mySelf.opensInDays != null
            ? `Your next self assessment opens in ${mySelf.opensInDays} day${mySelf.opensInDays === 1 ? '' : 's'}.`
            : 'Your goals are live. Your next self assessment opens near the end of the quarter.';

  // The annual review runs in the last 15 days of March. Surfaced only while it is
  // actually open - a banner that is up all year is wallpaper.
  const now = today();
  const { fyStartYear } = currentPeriod(now);
  const annualOpen = isAnnualReviewOpen(fyStartYear, now);
  const annualWindow = annualReviewWindow(fyStartYear);

  return (
    <PageContainer>
      {annualOpen && (
        <Alert>
          <CalendarCheck className="size-4" />
          <AlertDescription>
            <span className="font-medium">Annual review is open</span> until{' '}
            {formatPlainDate(annualWindow.closesOn)} — final ratings, promotion and salary
            recommendations for the year.
          </AlertDescription>
        </Alert>
      )}

      {/* --- Waiting on you ------------------------------------------------- */}
      {myGoalsUnpublished && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              Your goals for this cycle have not been published yet, so nothing you do is being
              measured against them. Ask your reporting manager to publish them.
            </span>
            <Button hint={"Open your goals"} asChild size="sm">
              <Link to="/goals">
                View goals <ArrowRight className="ml-1 size-3.5" />
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {selfOpen && mySelf?.exists && (
        // Amber only when HR forced the window - a manual override is something the
        // employee should notice. A routine open window is ordinary, not a warning.
        <Alert variant={mySelf.windowReason === 'FORCED_OPEN' ? 'warning' : undefined}>
          <CalendarCheck className="size-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              <span className="font-medium">Your {mySelf.quarterLabel} self assessment is open.</span>{' '}
              {mySelf.windowReason === 'FORCED_OPEN'
                ? 'HR has opened this window manually — submit while it is open.'
                : mySelf.closesInDays != null
                  ? `You have ${mySelf.closesInDays} day${mySelf.closesInDays === 1 ? '' : 's'} to fill it in.`
                  : 'Submit it before the window closes.'}
            </span>
            <Button hint={"Start your self-assessment"} asChild size="sm">
              <Link to={`/assessments?quarter=${mySelf.quarterId}`}>
                Start it <ArrowRight className="ml-1 size-3.5" />
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Above the team's work, like every other row that is blocking THIS person: the
          whole chain stops here until they answer, and the only other prompt they get
          is an email. */}
      {awaitingMyAcceptance && mySelf?.exists && (
        <Alert>
          <CheckCircle2 className="size-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              <span className="font-medium">
                Your manager has completed your {mySelf.quarterLabel} review.
              </span>{' '}
              Accept the rating, or send it back with a reason.
            </span>
            <Button hint={"Review this assessment"} asChild size="sm">
              <Link to={`/assessments?quarter=${mySelf.quarterId}`}>
                Review it <ArrowRight className="ml-1 size-3.5" />
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {myReviews.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Waiting on you</CardTitle>
              <CardDescription>
                {myReviews.length} review{myReviews.length === 1 ? '' : 's'}
                {overdue > 0 ? ` · ${overdue} past the 3-day deadline` : ''}
              </CardDescription>
            </div>
            <Button hint={"See everything waiting for you"} asChild variant="outline" size="sm">
              <Link to="/team/reviews">See all</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {myReviews.slice(0, 5).map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{a.employeeName}</p>
                  <p className="text-xs text-muted-foreground">{a.quarterLabel}</p>
                </div>
                <Badge
                  variant={a.managerOverdue ? 'soft-destructive' : assessmentStatusVariant(a.status)}
                  className={PILL_CLASS}
                >
                  {a.myPendingStage ? STAGE_LABELS[a.myPendingStage] : '—'}
                </Badge>
                <Button hint={"Review this assessment"} asChild size="sm" variant="outline">
                  <Link to={`/team/${a.employeeId}/assessment?quarter=${a.quarterId}`}>Review</Link>
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Below everything that is blocking this person: onboarding can wait a day, a
          review deadline cannot. */}
      <GetStartedCard />

      {nothingToDo && (
        <Card>
          <CardContent>
            <EmptyState art={TasksArt} title="Nothing needs you right now" body={emptyStateMessage} />
          </CardContent>
        </Card>
      )}

      {/* --- Your own cycle -------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your cycle</CardTitle>
          <CardDescription>
            {goalSet?.cycleLabel ?? '—'} · {goalSet?.goals.length ?? 0} goals
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <StatTile
            label="Organization goals"
            value={goalSet?.validation.orgCount ?? 0}
            tone={goalSet?.status === 'ACCEPTED' ? 'good' : 'default'}
            caption={
              goalSet?.status === 'ACCEPTED'
                ? `accepted · ${goalSet.validation.orgTotalWeightage}% weightage`
                : goalSet?.status === 'PENDING_ACCEPTANCE'
                  ? 'awaiting your acceptance'
                  : 'not yet assigned'
            }
          />
          <StatTile
            label="Development goals"
            value={goalSet?.goals.filter((g) => g.type === 'INDIVIDUAL').length ?? 0}
            caption="0% weightage, shown at appraisal"
          />
          <StatTile
            label="Direct reports"
            value={me.directReportCount}
            caption={me.hasReports ? 'you review their assessments' : 'you have no reports'}
          />
        </CardContent>
      </Card>

      {/*
        Where YOUR quarter is, not a diagram of the process.

        This used to render `completed={[]} active={null}` — the same five grey dots for
        every person on every day of the year, which answers a question nobody opens a
        dashboard to ask. The status is already on screen twice over; the one thing this
        shape is good at is saying whose turn it is, so it says that.
      */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Review chain</CardTitle>
          <CardDescription>
            {myStage
              ? `Your ${mySelf?.exists ? mySelf.quarterLabel : ''} assessment is at ${STAGE_LABELS[myStage]}.`
              : mySelf?.exists && mySelf.status === 'COMPLETED'
                ? `Your ${mySelf.quarterLabel} assessment is complete.`
                : mySelf?.exists
                  ? `${mySelf.quarterLabel} runs ${chainSummary(mySelf.chain)}. Department heads have oversight but are not an approval step.`
                  : 'HR sets the stages each quarter runs. Department heads have oversight but are not an approval step.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StageTracker
            chain={mySelf?.exists ? mySelf.chain : undefined}
            completed={mySelf?.exists ? completedStages(mySelf.status, mySelf.chain) : []}
            active={myStage}
            horizontal
          />
        </CardContent>
      </Card>

      {/* --- Your team ------------------------------------------------------- */}
      {me.hasReports && teamRows.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Your team</CardTitle>
              <CardDescription>
                {teamGoalsPending === 0
                  ? 'everyone has accepted their goals'
                  : `${teamGoalsPending} still without accepted goals`}
              </CardDescription>
            </div>
            <Button hint={"Open your team"} asChild variant="outline" size="sm">
              <Link to="/team">Open team</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {teamRows.map((r) => (
              <div key={r.employeeId} className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.name}</p>
                </div>
                {!r.goalsAccepted && (
                  <Badge variant="soft-warning" className={PILL_CLASS}>
                    Goals not accepted
                  </Badge>
                )}
                {r.assessmentStatus && (
                  <Badge
                    variant={r.overdue ? 'soft-destructive' : assessmentStatusVariant(r.assessmentStatus as never)}
                    className={PILL_CLASS}
                  >
                    {r.assessmentStatus.replace(/_/g, ' ').toLowerCase()}
                  </Badge>
                )}
                {r.rating !== null && <span className="pms-num text-sm font-semibold">{r.rating}</span>}
                <Button hint={"Open goals"} asChild size="sm" variant="ghost">
                  <Link to={`/team/${r.employeeId}/goals`}>Goals</Link>
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* --- Recognition suggestions ------------------------------------------ */}
      {me.hasReports && suggestions && suggestions.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Worth recognising</CardTitle>
              <CardDescription>
                Rated well and not thanked lately. Suggestions only — recognition a system hands out
                on a schedule stops meaning anything.
              </CardDescription>
            </div>
            <Button hint={"Appreciate a colleague's work"} asChild variant="outline" size="sm">
              <Link to="/recognition">Recognise</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.employeeId} className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{s.reason}</p>
                </div>
                {s.rating !== null && (
                  <span className="pms-num text-sm font-semibold">{s.rating}</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* --- Org-wide, HR only ------------------------------------------------ */}
      {isHr && overview && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Employees"
            value={overview.employees}
            tone={overview.needsOnboarding > 0 ? 'warning' : 'default'}
            caption={
              overview.needsOnboarding > 0
                ? `${overview.needsOnboarding} missing a department or manager`
                : 'all set up'
            }
          />
          <StatTile
            label="In the review chain"
            value={overview.awaitingManager + overview.awaitingHr + overview.awaitingManagement}
            caption={`${overview.awaitingManager} manager · ${overview.awaitingHr} HR · ${overview.awaitingManagement} management`}
          />
          <StatTile
            label="Overdue"
            value={overview.overdueManagerReviews}
            tone={overview.overdueManagerReviews > 0 ? 'critical' : 'good'}
            caption={
              overview.overdueManagerReviews > 0 ? 'past the 3-day deadline' : 'nothing overdue'
            }
          />
          <StatTile
            label="Average rating"
            value={overview.averageRating ?? '—'}
            caption={
              overview.averageRating === null
                ? 'no final ratings yet'
                : `${overview.assessmentsCompleted} completed`
            }
          />
        </div>
      )}
    </PageContainer>
  );
}
